<template>
  <div class="min-h-screen bg-slate-50">
    <Header />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div
        class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8"
      >
        <div>
          <h1 class="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <div
              class="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center"
            >
              <svg
                class="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z"
                />
              </svg>
            </div>

            Reward Points
          </h1>

          <p class="mt-2 text-gray-500">
            View your earned and redeemed reward points.
          </p>
        </div>

        <!-- Total Balance -->
        <div
          class="rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 shadow-lg"
        >
          <p class="text-sm opacity-80">Available Balance</p>

          <h2 class="text-3xl font-bold mt-1">
            {{ stats.total }}

            <span class="text-lg font-medium"> pts </span>
          </h2>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <!-- Total -->
        <div
          class="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition"
        >
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs uppercase text-gray-400 font-semibold">Total</p>

              <h3 class="text-3xl font-bold text-indigo-600 mt-2">
                {{ stats.total }}
              </h3>

              <p class="text-xs text-gray-400 mt-1">Balance Points</p>
            </div>

            <div
              class="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center"
            >
              ⚡
            </div>
          </div>
        </div>

        <!-- Earned -->
        <div
          class="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition"
        >
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs uppercase text-gray-400 font-semibold">
                Earned
              </p>

              <h3 class="text-3xl font-bold text-green-600 mt-2">
                {{ stats.earned }}
              </h3>

              <p class="text-xs text-gray-400 mt-1">Credited</p>
            </div>

            <div
              class="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center"
            >
              📈
            </div>
          </div>
        </div>

        <!-- Used -->
        <div
          class="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition"
        >
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs uppercase text-gray-400 font-semibold">Used</p>

              <h3 class="text-3xl font-bold text-red-500 mt-2">
                {{ stats.used }}
              </h3>

              <p class="text-xs text-gray-400 mt-1">Redeemed</p>
            </div>

            <div
              class="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center"
            >
              🎁
            </div>
          </div>
        </div>

        <!-- Activities -->
        <div
          class="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition"
        >
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs uppercase text-gray-400 font-semibold">
                Activities
              </p>

              <h3 class="text-3xl font-bold text-purple-600 mt-2">
                {{ stats.activities }}
              </h3>

              <p class="text-xs text-gray-400 mt-1">Total Records</p>
            </div>

            <div
              class="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center"
            >
              📋
            </div>
          </div>
        </div>
      </div>

      <!-- Main Card -->
      <div class="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
        <!-- Tabs -->
        <div
          class="flex flex-wrap items-center gap-2 border-b border-gray-100 pb-5 mb-6"
        >
          <button
            v-for="tab in tabs"
            :key="tab.key"
            @click="currentTab = tab.key"
            class="px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300"
            :class="
              currentTab === tab.key
                ? 'bg-indigo-600 text-white shadow'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            "
          >
            {{ tab.label }}

            <span v-if="tab.key !== 'all'" class="ml-1 text-xs opacity-80">
              ({{ getTabCount(tab.key) }})
            </span>
          </button>
        </div>

        <!-- Activity List Starts Here -->
        <div class="space-y-4">
          <!-- Activity List -->
          <div
            v-for="(activity, index) in filteredActivities"
            :key="activity.id || index"
            class="group flex items-center justify-between rounded-2xl border border-gray-100 bg-white p-4 hover:border-indigo-200 hover:shadow-md transition-all duration-300"
          >
            <!-- Left -->
            <div class="flex items-center gap-4 min-w-0">
              <!-- Icon -->
              <div
                :class="[
                  'w-12 h-12 rounded-xl flex items-center justify-center',
                  activity.debit_name ? 'bg-red-100' : 'bg-blue-100',
                ]"
              >
                <svg
                  class="w-6 h-6"
                  :class="
                    activity.debit_name ? 'text-red-600' : 'text-blue-600'
                  "
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    :d="getIconPath(activity)"
                  />
                </svg>
              </div>

              <!-- Details -->
              <div class="min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <h3 class="font-semibold text-slate-800 truncate">
                    {{
                      activity.name || activity.debit_name || "Reward Activity"
                    }}
                  </h3>

                  <span
                    :class="
                      activity.debit_name
                        ? 'bg-red-100 text-red-600'
                        : 'bg-green-100 text-green-600'
                    "
                    class="px-2 py-1 rounded-full text-[10px] font-bold uppercase"
                  >
                    {{ activity.debit_name ? "Used" : "Earned" }}
                  </span>
                </div>

                <div
                  class="flex items-center gap-2 mt-1 text-xs text-gray-500 flex-wrap"
                >
                  <span>
                    {{ capitalize(activity.title || "General") }}
                  </span>

                  <span>•</span>

                  <span>
                    {{ formatTimeAgo(activity.created_at) }}
                  </span>

                  <span>•</span>

                  <span
                    :class="
                      activity.status &&
                      activity.status.toLowerCase() === 'completed'
                        ? 'text-green-600'
                        : 'text-yellow-600'
                    "
                    class="font-medium"
                  >
                    {{ activity.status || "Completed" }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Right -->
            <div class="text-right">
              <p
                :class="activity.debit_name ? 'text-red-600' : 'text-green-600'"
                class="text-xl font-bold"
              >
                {{ activity.debit_name ? "-" : "+" }}{{ activity.points }}
              </p>

              <span class="text-xs text-gray-400"> Points </span>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="filteredActivities.length === 0" class="py-16 text-center">
            <div
              class="mx-auto w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center"
            >
              <svg
                class="w-10 h-10 text-gray-400"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9.75 9.75h.008v.008H9.75V9.75Zm4.5 0h.008v.008h-.008V9.75Zm-7.5 2.25a7.5 7.5 0 1 1 15 0 7.5 7.5 0 0 1-15 0Zm3.75 3.75c.75.75 1.875 1.125 3 1.125s2.25-.375 3-1.125"
                />
              </svg>
            </div>

            <h3 class="mt-6 text-lg font-semibold text-slate-700">
              No Reward History
            </h3>

            <p class="mt-2 text-sm text-gray-500">
              There are no reward activities available for this filter.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "@/components/Student/Header.vue";
import axios from "axios";
import { mapGetters } from "vuex";

export default {
  name: "ActivityPoints",
  components: {
    Header,
  },
  data() {
    return {
      currentTab: "all",
      tabs: [
        { key: "all", label: "All History" },
        { key: "earned", label: "Earned" },
        { key: "used", label: "Used" },
      ],
      activities: [],
      stats: {
        total: 0,
        earned: 0,
        used: 0,
        pending: 0,
        activities: 0,
      },
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters(["userInfo"]),
    filteredActivities() {
      if (this.currentTab === "all") {
        return this.activities;
      }
      if (this.currentTab === "earned") {
        return this.activities.filter((activity) => !activity.debit_name);
      }
      if (this.currentTab === "used") {
        return this.activities.filter((activity) => activity.debit_name);
      }
      return this.activities.filter(
        (activity) =>
          activity.title?.toLowerCase() === this.currentTab.toLowerCase(),
      );
    },
    activityBreakdown() {
      const breakdown = {};
      this.activities.forEach((activity) => {
        const type = activity.title || "unknown";
        breakdown[type] = (breakdown[type] || 0) + 1;
      });
      return breakdown;
    },
  },
  created() {
    this.fetchActivities();
  },
  methods: {
    async fetchActivities(page = 1) {
      if (this.isLoading) return;
      this.isLoading = true;

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/reward-history-list/?Key=${this.userInfo.token_key}&page=${page}`,
        );

        if (response.data.status === 200) {
          const data = response.data.data;
          this.activities = data.data?.data || [];
          this.calculateStats();
        } else {
          this.activities = [];
          this.resetStats();
        }
      } catch (error) {
        console.error("Error fetching activities:", error);
        this.activities = [];
        this.resetStats();
      } finally {
        this.isLoading = false;
      }
    },

    calculateStats() {
      let earned = 0;
      let used = 0;
      let pending = 0;

      this.activities.forEach((activity) => {
        const points = activity.points || 0;
        if (activity.debit_name) {
          used += points;
        } else {
          earned += points;
        }

        if (!activity.status || activity.status.toLowerCase() === "pending") {
          pending += points;
        }
      });

      this.stats = {
        total: earned - used,
        earned: earned,
        used: used,
        pending: pending,
        activities: this.activities.length,
      };
    },

    resetStats() {
      this.stats = { total: 0, earned: 0, used: 0, pending: 0, activities: 0 };
    },

    getTabCount(key) {
      if (key === "all") return this.activities.length;
      if (key === "earned") {
        return this.activities.filter((activity) => !activity.debit_name)
          .length;
      }
      if (key === "used") {
        return this.activities.filter((activity) => activity.debit_name).length;
      }
      return this.activities.filter(
        (activity) => activity.title?.toLowerCase() === key.toLowerCase(),
      ).length;
    },

    capitalize(str) {
      if (!str) return "";
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    formatTimeAgo(dateString) {
      if (!dateString) return "Recently";

      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    },

    getIconPath(activity) {
      const title = activity.title?.toLowerCase() || "default";
      const paths = {
        lesson:
          "M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25",
        assessment:
          "M9 12h3.75M9 15h3.75M9 9h3.75M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9Z",
        assignment:
          "M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z",
        quiz: "M12 18a3.75 3.75 0 0 0 .495-7.467 5.99 5.99 0 0 0-1.925-3.546 5.974 5.974 0 0 0-2.133-1A3.75 3.75 0 0 0 3.75 9.75c0 .358.05.705.144 1.037A3.75 3.75 0 0 0 4.5 18h7.5ZM12 18v-1.5m0 1.5v1.5m0-1.5h1.5m-1.5 0H10.5m5.412-3.085a3.75 3.75 0 0 0-.412-6.415 5.976 5.976 0 0 0-1.636-2.71 5.974 5.974 0 0 0-2.133 1A3.75 3.75 0 0 0 12 9.75c0 .358.05.705.144 1.037A3.75 3.75 0 0 0 12.75 18h2.662Z",
        default: "m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z",
      };
      return paths[title] || paths.default;
    },
  },
};
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
</style>
